import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  Output,
  Renderer2
} from '@angular/core';

import { DndConfig } from './dnd-config';
import { DndHandleDirective } from './dnd-handle.directive';
import { DroppableDirective } from './droppable.directive';
import { DataTransferEffect, DndDropEvent, EventTypes } from './models';
import { StateService } from './state.service';
import { DndEvent, indexOfElement, setDragImage } from './utils';

@Directive({
  selector: '[pcsDraggable]'
})
export class DraggableDirective {
  @Input() pcsDraggable: any;

  @Output() readonly dndStart: EventEmitter<DragEvent> = new EventEmitter<DragEvent>();
  @Output() readonly dndEnd: EventEmitter<DragEvent> = new EventEmitter<DragEvent>();
  @Output() readonly dndMoved: EventEmitter<DndDropEvent> = new EventEmitter<DndDropEvent>();
  @Output() readonly dndCanceled: EventEmitter<DragEvent> = new EventEmitter<DragEvent>();

  private dndHandle: DndHandleDirective;

  @Input()
  set isDisabled(value: boolean) {
    this.draggable = !value;

    if (this.draggable) {
      this.renderer.removeClass(this.element, this.config.draggableDisabledClass);
    } else {
      this.renderer.addClass(this.element, this.config.draggableDisabledClass);
    }
  }

  get element() {
    return this.elementRef.nativeElement;
  }

  get container() {
    return this.droppableContainer.container;
  }

  @HostBinding('attr.draggable') draggable = true;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private stateService: StateService,
    private droppableContainer: DroppableDirective,
    @Inject(DndConfig) private config
  ) {
  }

  @HostListener('dragstart', ['$event'])
  onDragStart(event: DndEvent) {
    if (!this.draggable) {
      return false;
    }

    // check if there is drag handle
    if (this.dndHandle && !event._dndUsingHandle) {
      return false;
    }

    this.stateService.startDrag(event, this.pcsDraggable, indexOfElement(this.container, this.element));
    setDragImage(event, this.getDragImage());

    const unregister = this.renderer.listen(this.element, EventTypes.Drag, () => {
      this.renderer.addClass(this.element, this.config.draggingSourceClass);
      unregister();
    });

    this.dndStart.emit(event);
    event.stopPropagation();
  }

  @HostListener('dragend', ['$event'])
  onDragEnd(event: DragEvent) {
    const dropEffect = this.stateService.dragState.dropEffect;

    if (dropEffect === DataTransferEffect.Move) {
      this.dndMoved.emit({event, data: this.pcsDraggable});
    } else {
      this.dndCanceled.emit(event);
    }

    this.dndEnd.emit(event);
    this.stateService.endDrag();
    this.renderer.removeClass(this.element, this.config.draggingSourceClass);

    event.stopPropagation();
  }

  registerDragHandle(handle: DndHandleDirective) {
    this.dndHandle = handle;
  }

  private getDragImage() {
    return this.element;
  }
}
